import * as React from 'react'
import type { Table } from '@tanstack/react-table'
import { Download, Upload, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DataTableViewOptions } from './data-table-view-options'

interface DataTableToolbarProps<TData> {
  table: Table<TData>
  filterPlaceholder?: string
  // For global search across multiple columns
  globalFilter?: string
  setGlobalFilter?: (value: string) => void
  // For single column search (legacy support)
  searchKey?: string
  // Filter components (faceted filters)
  children?: React.ReactNode
  // Import functionality
  onImport?: () => void
  importLabel?: string
  // Export functionality
  onExport?: () => void
  exportLabel?: string
}

export function DataTableToolbar<TData>({
  table,
  filterPlaceholder = 'Search...',
  globalFilter,
  setGlobalFilter,
  searchKey,
  children,
  onImport,
  importLabel = 'Import',
  onExport,
  exportLabel = 'Export',
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0 || !!globalFilter

  // Determine if using global search or single column search
  const useGlobalSearch = globalFilter !== undefined && setGlobalFilter !== undefined

  return (
    <div className="flex flex-col gap-4 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
        {/* Search Input */}
        {useGlobalSearch ? (
          <Input
            placeholder={filterPlaceholder}
            value={globalFilter}
            onChange={(event) => setGlobalFilter(event.target.value)}
            className="h-8 w-full sm:w-[150px] lg:w-[250px]"
          />
        ) : searchKey ? (
          <Input
            placeholder={filterPlaceholder}
            value={(table.getColumn(searchKey)?.getFilterValue() as string) ?? ''}
            onChange={(event) =>
              table.getColumn(searchKey)?.setFilterValue(event.target.value)
            }
            className="h-8 w-full sm:w-[150px] lg:w-[250px]"
          />
        ) : null}

        {/* Filter components passed as children */}
        <div className="flex flex-wrap gap-2">
          {children}
        </div>

        {/* Reset filters button */}
        {isFiltered && (
          <Button
            variant="ghost"
            onClick={() => {
              table.resetColumnFilters()
              if (setGlobalFilter) {
                setGlobalFilter('')
              }
            }}
            className="h-8 px-2 lg:px-3"
          >
            Reset
            <X className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Import button */}
        {onImport && (
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={onImport}
          >
            <Upload className="mr-2 h-4 w-4" />
            {importLabel}
          </Button>
        )}

        {/* Export button */}
        {onExport && (
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={onExport}
          >
            <Download className="mr-2 h-4 w-4" />
            {exportLabel}
          </Button>
        )}

        {/* Column visibility toggle */}
        <DataTableViewOptions table={table} />
      </div>
    </div>
  )
}
